/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Universal Force Field (UFF) Lennard-Jones parameters for every naturally
 * occurring element, from:
 *   Rappé, A. K.; Casewit, C. J.; Colwell, K. S.; Goddard, W. A. III; Skiff,
 *   W. M. "UFF, a full periodic table force field for molecular mechanics
 *   and molecular dynamics simulations." J. Am. Chem. Soc. 114, 10024 (1992).
 *
 * UFF tabulates x1 (van der Waals distance = r_min, in Å) and D1 (well depth,
 * in kcal/mol). The engine expects standard LJ σ (zero crossing) and ε in eV:
 *   σ = x1 · 2^(-1/6)
 *   ε = D1 · 0.0433641
 *
 * UFF prioritizes periodic-table coverage over accuracy. It is the right
 * baseline so every element behaves *like* an LJ particle out of the box;
 * specific systems that need accuracy override it: the species table
 * (speciesForceField.ts — noble gases, N₂, O₂, CO₂, methane, water) in the
 * Molecular Modeling engine, and the per-material packs in the crystal lab.
 *
 * Keys are uppercase element symbols, matching the convention in the
 * vendored chem library (src/lib/chem/Element.js).
 */

export interface UffLjParams {
  sigma: number; // Å — standard LJ σ (zero crossing)
  epsilon: number; // eV — well depth
}

export const UFF_LJ_PARAMETERS: Readonly<Record<string, UffLjParams>> = {
  H: { sigma: 2.5711, epsilon: 0.001908 },
  HE: { sigma: 2.1043, epsilon: 0.002428 },
  LI: { sigma: 2.1836, epsilon: 0.001084 },
  BE: { sigma: 2.4455, epsilon: 0.003686 },
  B: { sigma: 3.6375, epsilon: 0.007806 },
  C: { sigma: 3.4309, epsilon: 0.004553 },
  N: { sigma: 3.2607, epsilon: 0.002992 },
  O: { sigma: 3.1181, epsilon: 0.002602 },
  F: { sigma: 2.997, epsilon: 0.002168 },
  NE: { sigma: 2.8892, epsilon: 0.001821 },
  NA: { sigma: 2.6575, epsilon: 0.001301 },
  MG: { sigma: 2.6913, epsilon: 0.004813 },
  AL: { sigma: 4.0082, epsilon: 0.021899 },
  SI: { sigma: 3.8264, epsilon: 0.017432 },
  P: { sigma: 3.6946, epsilon: 0.013226 },
  S: { sigma: 3.5948, epsilon: 0.011882 },
  CL: { sigma: 3.5164, epsilon: 0.009844 },
  AR: { sigma: 3.446, epsilon: 0.008022 },
  K: { sigma: 3.3961, epsilon: 0.001518 },
  CA: { sigma: 3.0282, epsilon: 0.010321 },
  SC: { sigma: 2.9355, epsilon: 0.000824 },
  TI: { sigma: 2.8286, epsilon: 0.000737 },
  V: { sigma: 2.801, epsilon: 0.000694 },
  CR: { sigma: 2.6932, epsilon: 0.00065 },
  MN: { sigma: 2.638, epsilon: 0.000564 },
  FE: { sigma: 2.5943, epsilon: 0.000564 },
  CO: { sigma: 2.5586, epsilon: 0.000607 },
  NI: { sigma: 2.5248, epsilon: 0.00065 },
  CU: { sigma: 3.1137, epsilon: 0.000217 },
  ZN: { sigma: 2.4615, epsilon: 0.005377 },
  GA: { sigma: 3.9048, epsilon: 0.017996 },
  GE: { sigma: 3.8131, epsilon: 0.016435 },
  AS: { sigma: 3.7685, epsilon: 0.013399 },
  SE: { sigma: 3.7462, epsilon: 0.012619 },
  BR: { sigma: 3.732, epsilon: 0.010884 },
  KR: { sigma: 3.6892, epsilon: 0.00954 },
  RB: { sigma: 3.6652, epsilon: 0.001735 },
  SR: { sigma: 3.2438, epsilon: 0.010191 },
  Y: { sigma: 2.9801, epsilon: 0.003122 },
  ZR: { sigma: 2.7832, epsilon: 0.002992 },
  NB: { sigma: 2.8197, epsilon: 0.002559 },
  MO: { sigma: 2.719, epsilon: 0.002428 },
  TC: { sigma: 2.6709, epsilon: 0.002081 },
  RU: { sigma: 2.6397, epsilon: 0.002428 },
  RH: { sigma: 2.6094, epsilon: 0.002298 },
  PD: { sigma: 2.5827, epsilon: 0.002081 },
  AG: { sigma: 2.8045, epsilon: 0.001561 },
  CD: { sigma: 2.5373, epsilon: 0.009887 },
  IN: { sigma: 3.9761, epsilon: 0.025975 },
  SN: { sigma: 3.9128, epsilon: 0.024588 },
  SB: { sigma: 3.9378, epsilon: 0.01947 },
  TE: { sigma: 3.9823, epsilon: 0.017259 },
  I: { sigma: 4.009, epsilon: 0.0147 },
  XE: { sigma: 3.9235, epsilon: 0.014397 },
  CS: { sigma: 4.0242, epsilon: 0.001951 },
  BA: { sigma: 3.299, epsilon: 0.015785 },
  LA: { sigma: 3.1377, epsilon: 0.000737 },
  CE: { sigma: 3.168, epsilon: 0.000564 },
  PR: { sigma: 3.2125, epsilon: 0.000434 },
  ND: { sigma: 3.1849, epsilon: 0.000434 },
  PM: { sigma: 3.1599, epsilon: 0.00039 },
  SM: { sigma: 3.1359, epsilon: 0.000347 },
  EU: { sigma: 3.1118, epsilon: 0.000347 },
  GD: { sigma: 3.0005, epsilon: 0.00039 },
  TB: { sigma: 3.0745, epsilon: 0.000304 },
  DY: { sigma: 3.054, epsilon: 0.000304 },
  HO: { sigma: 3.037, epsilon: 0.000304 },
  ER: { sigma: 3.021, epsilon: 0.000304 },
  TM: { sigma: 3.0059, epsilon: 0.00026 },
  YB: { sigma: 2.9889, epsilon: 0.009887 },
  LU: { sigma: 3.2429, epsilon: 0.001778 },
  HF: { sigma: 2.7984, epsilon: 0.003122 },
  TA: { sigma: 2.8243, epsilon: 0.003513 },
  W: { sigma: 2.7342, epsilon: 0.002905 },
  RE: { sigma: 2.6318, epsilon: 0.002862 },
  OS: { sigma: 2.7797, epsilon: 0.001604 },
  IR: { sigma: 2.5302, epsilon: 0.003166 },
  PT: { sigma: 2.4536, epsilon: 0.003469 },
  AU: { sigma: 2.9337, epsilon: 0.001691 },
  HG: { sigma: 2.4099, epsilon: 0.016695 },
  TL: { sigma: 3.8727, epsilon: 0.029488 },
  PB: { sigma: 3.8282, epsilon: 0.02875 },
  BI: { sigma: 3.8932, epsilon: 0.022463 },
  PO: { sigma: 4.1952, epsilon: 0.014093 },
  AT: { sigma: 4.2318, epsilon: 0.012315 },
  RN: { sigma: 4.2451, epsilon: 0.010754 },
  FR: { sigma: 4.3654, epsilon: 0.002168 },
  RA: { sigma: 3.2755, epsilon: 0.017519 },
  AC: { sigma: 3.0985, epsilon: 0.001431 },
  TH: { sigma: 3.0254, epsilon: 0.001128 },
  PA: { sigma: 3.0504, epsilon: 0.000954 },
  U: { sigma: 3.0245, epsilon: 0.000954 },
  NP: { sigma: 3.0504, epsilon: 0.000824 },
  PU: { sigma: 3.0504, epsilon: 0.000694 },
  AM: { sigma: 3.0121, epsilon: 0.000607 },
  CM: { sigma: 2.9631, epsilon: 0.000564 },
  BK: { sigma: 2.9747, epsilon: 0.000564 },
  CF: { sigma: 2.9515, epsilon: 0.000564 },
  ES: { sigma: 2.939, epsilon: 0.00052 },
  FM: { sigma: 2.9274, epsilon: 0.00052 },
  MD: { sigma: 2.9167, epsilon: 0.000477 },
  NO: { sigma: 2.8935, epsilon: 0.000477 },
  LR: { sigma: 2.8828, epsilon: 0.000477 },
};

export function getUffLj(elementSymbol: string): UffLjParams | undefined {
  return UFF_LJ_PARAMETERS[elementSymbol.toUpperCase()];
}
